import React from 'react';
import PropTypes from 'prop-types';
import { Trash2 } from 'lucide-react';
import GoalCheck from './GoalCheck';

// Kontextzeile unter dem Zieltext: woher stammt das Ziel?
const formatContext = (goal) => {
  const session = goal.tenant_sessions;
  if (!session) return goal.isLongTerm ? 'Langzeitziel' : 'Ohne Session';

  const raw = session.createdAt || goal.createdAt;
  const date = raw ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) return session.topic || 'Session';

  const sameYear = date.getFullYear() === new Date().getFullYear();
  const formatted = date.toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' })
  });
  return session.topic ? `${session.topic} · ${formatted}` : formatted;
};

/**
 * Eine Ziel-Zeile im Logbuch (Okt 2026, wie im Spot, Motion-Kit `.eo-goal-row`):
 * runder Haken · Zieltext · Kontext · Loeschen.
 *
 * Beim Abhaken waechst die lila Fuellung aus der Mitte, der Haken erscheint,
 * dann laeuft ein Strich durch den Text und er wird grau (`.goal-strike`).
 * Session-Ziele und freie Ziele sehen gleich aus; der Unterschied steht als
 * Text in der Kontextzeile.
 */
const GoalRow = ({ goal, onToggle, onDelete, busy = false }) => {
  const { completed } = goal;

  return (
    <div className="group flex items-start gap-3 px-0.5 py-[11px]">
      <button
        type="button"
        onClick={() => onToggle(goal)}
        disabled={busy}
        aria-pressed={completed}
        aria-label={completed ? `„${goal.text}" als offen markieren` : `„${goal.text}" als erreicht markieren`}
        className="-m-2.5 flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-full p-2.5 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <GoalCheck completed={completed} />
      </button>

      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-body-s">
          <span className={`goal-strike ${completed ? 'is-done text-muted-foreground' : 'text-foreground'}`}>
            {goal.text}
          </span>
        </p>
        {goal.description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{goal.description}</p>
        )}
        <p className="mt-0.5 text-xs text-faint">{formatContext(goal)}</p>
      </div>

      <button
        type="button"
        onClick={() => onDelete(goal)}
        disabled={busy}
        aria-label={`„${goal.text}" löschen`}
        // Auf Mobile dauerhaft sichtbar (kein Hover), auf Desktop erst bei Hover/Focus
        className="-m-2 flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-xl p-2 text-faint transition-colors duration-base hover:text-danger focus-visible:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

GoalRow.propTypes = {
  goal: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  busy: PropTypes.bool
};

export default GoalRow;
